import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface ServiceCatalogItem {
  id: string;
  owner_id: string;
  name: string;
  description: string;
  default_price: number;
  vat_rate: number;
  active: boolean;
  created_at: string;
}

export function useServiceCatalog() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['service_catalog', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('service_catalog')
        .select('*')
        .eq('owner_id', user!.id)
        .eq('active', true)
        .order('name');
      if (error) throw error;
      return (data || []) as ServiceCatalogItem[];
    },
    enabled: !!user,
  });
}

export function useSaveServiceCatalogItem() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (item: Partial<ServiceCatalogItem> & { name: string }) => {
      if (item.id) {
        const { error } = await (supabase as any)
          .from('service_catalog')
          .update({
            name: item.name,
            description: item.description || '',
            default_price: item.default_price || 0,
            vat_rate: item.vat_rate || 0,
            active: item.active ?? true,
          })
          .eq('id', item.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from('service_catalog')
          .insert({
            owner_id: user!.id,
            name: item.name,
            description: item.description || '',
            default_price: item.default_price || 0,
            vat_rate: item.vat_rate || 0,
            active: true,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service_catalog'] }),
  });
}

export function useDeleteServiceCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('service_catalog')
        .update({ active: false })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service_catalog'] }),
  });
}
